import type { MonthlyTimelineItem } from './types';

export type EventHighlightMode = 'single-click' | 'filter';

export interface EventHighlightSetResult {
  highlightSet: Set<string>;
  parentToUnnest: Set<string>;
}

function getShowOnTimelineSuffix(
  mode: EventHighlightMode,
  showOnTimeline: boolean
): string {
  return mode === 'filter' ? 'false' : String(showOnTimeline);
}

/**
 * Builds highlight keys from an event's releaseHierarchyPaths.
 * Mirrors TimelineCard / RoadmapCard single-click vs double-click (filter) behavior.
 */
export function buildEventHighlightSet(
  item: MonthlyTimelineItem,
  mode: EventHighlightMode
): EventHighlightSetResult {
  const highlightSet = new Set<string>();
  const parentToUnnest = new Set<string>();
  const issueId = item.issueId || '';
  const showOnTimeline = Boolean(item.showOnTimeline);
  const suffix = getShowOnTimelineSuffix(mode, showOnTimeline);

  const paths = item.relationship?.releaseHierarchyPaths;
  if (paths && paths.length > 0) {
    paths.forEach(path => {
      if (!path.issue) return;

      if (path.phaseId) {
        highlightSet.add(`${path.issue}-${path.phaseId}-${suffix}`);
      } else if (path.phase) {
        highlightSet.add(`${path.issue}-${path.phase}-${suffix}`);
      } else {
        highlightSet.add(path.issue);
      }

      if (path.parent && path.parent !== issueId) {
        parentToUnnest.add(path.parent);
        highlightSet.add(path.parent);
      }
    });
  }

  if (issueId) {
    if (mode === 'filter') {
      highlightSet.add(issueId);
    } else if (item.releasePhaseId) {
      highlightSet.add(`${issueId}-${item.releasePhaseId}-${showOnTimeline}`);
    } else if (item.releasePhase) {
      highlightSet.add(`${issueId}-${item.releasePhase}-${showOnTimeline}`);
    } else {
      highlightSet.add(issueId);
    }
  }

  return { highlightSet, parentToUnnest };
}

export function tbdMatchesHighlightSet(
  tbdData: Array<{ project?: Array<Record<string, unknown>> }>,
  highlightSet: Set<string>
): boolean {
  return tbdData.some(entry =>
    (entry.project || []).some(project => {
      const projectIssueId = project.issueId as string | undefined;
      if (!projectIssueId) return false;

      if (highlightSet.has(projectIssueId)) return true;

      const releasePhaseId = project.releasePhaseId as string | undefined;
      if (releasePhaseId) {
        const compositeKey = `${projectIssueId}-${releasePhaseId}-false`;
        if (highlightSet.has(compositeKey)) return true;
      }

      const releasePhase = project.releasePhase as string | undefined;
      if (releasePhase) {
        const compositeKey = `${projectIssueId}-${releasePhase}-false`;
        if (highlightSet.has(compositeKey)) return true;
      }

      return false;
    })
  );
}
